import { useEffect, useState } from "react";
import { api } from "../api";

const initialForm = {
  fullName: "",
  phone: "",
  email: "",
  eventDate: "",
  startTime: "",
  endTime: "",
  eventLocation: "",
  paymentMethod: "Transferencia",
  withoutAdvance: false,
  totalAmount: 0,
  advanceAmount: 0,
  notes: "",
};

export default function Contracts() {
  const [form, setForm] = useState(initialForm);
  const [proof, setProof] = useState(null);
  const [message, setMessage] = useState("");
  const [reservedDates, setReservedDates] = useState([]);

  useEffect(() => {
    api.get("/public/available-dates")
      .then((r) => setReservedDates(r.data.reservedDates || []))
      .catch(() => {});
  }, []);

  async function submit(e) {
    e.preventDefault();
    setMessage("");

    const payload = new FormData();
    payload.append("fullName", form.fullName);
    payload.append("phone", form.phone);
    payload.append("email", form.email);
    payload.append("eventDate", form.eventDate);
    payload.append("startTime", form.startTime);
    payload.append("endTime", form.endTime);
    payload.append("eventLocation", form.eventLocation);
    payload.append("paymentMethod", form.paymentMethod);
    payload.append("withoutAdvance", form.withoutAdvance ? "true" : "false");
    payload.append("totalAmount", String(form.totalAmount));
    payload.append("advanceAmount", String(form.advanceAmount));
    payload.append("notes", form.notes || "");
    if (proof) payload.append("paymentProof", proof);

    try {
      const res = await api.post("/contracts", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const code = res.data.validationCode;
      setMessage(`Reserva enviada. Codigo de validacion: ${code}. Puedes validar tu contrato en /validar/${code}`);
      setForm(initialForm);
      setProof(null);
      const datesRes = await api.get("/public/available-dates");
      setReservedDates(datesRes.data.reservedDates || []);
    } catch (err) {
      const data = err?.response?.data;
      let serverMsg = "";
      if (data?.errors) {
        const fieldErrors = Object.entries(data.errors)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`)
          .join(" | ");
        serverMsg = fieldErrors;
      } else {
        serverMsg = data?.message || data?.title || err?.message || "No fue posible guardar la reserva.";
      }
      setMessage(serverMsg);
    }
  }

  const isDateReserved = (dateStr) => reservedDates.some((d) => d.startsWith(dateStr));

  return (
    <section className="space-y-8">
      <div className="rounded-2xl border border-orange-400/20 bg-black/20 p-6">
        <h1 className="text-2xl font-bold uppercase tracking-wide">Reservar Contrato</h1>

        {message && (
          <p className="mt-3 rounded-xl border border-orange-500/40 bg-black/30 p-3 text-sm">{message}</p>
        )}

        <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={submit}>
          <input
            className="rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
            placeholder="Nombre completo"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            required
          />
          <input
            className="rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
            placeholder="Telefono"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            required
          />
          <input
            className="rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
            type="email"
            placeholder="Correo electronico"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
          <div className="relative">
            <input
              className={`w-full rounded-lg border bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none ${
                form.eventDate && isDateReserved(form.eventDate)
                  ? "border-red-500"
                  : "border-orange-400/20"
              }`}
              type="date"
              value={form.eventDate}
              onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
              required
            />
            {form.eventDate && isDateReserved(form.eventDate) && (
              <span className="absolute -bottom-5 left-0 text-xs text-red-400">Esta fecha ya esta reservada</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-orange-100/60">Hora inicio</label>
              <input
                className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-3 focus:border-orange-400 focus:outline-none"
                type="time"
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-orange-100/60">Hora fin</label>
              <input
                className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-3 focus:border-orange-400 focus:outline-none"
                type="time"
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                required
              />
            </div>
          </div>
          <input
            className="rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none md:col-span-2"
            placeholder="Ubicacion del evento"
            value={form.eventLocation}
            onChange={(e) => setForm({ ...form, eventLocation: e.target.value })}
            required
          />
          <select
            className="rounded-lg border border-orange-400/20 bg-black/30 p-3 focus:border-orange-400 focus:outline-none"
            value={form.paymentMethod}
            onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
          >
            <option value="Transferencia">Transferencia</option>
            <option value="Deposito">Deposito</option>
            <option value="Efectivo">Efectivo</option>
          </select>
          <input
            className="rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
            type="number"
            min="0"
            step="0.01"
            placeholder="Monto total"
            value={form.totalAmount || ""}
            onChange={(e) => setForm({ ...form, totalAmount: Number(e.target.value) })}
          />
          <input
            className="rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
            type="number"
            min="0"
            step="0.01"
            placeholder="Anticipo"
            value={form.advanceAmount || ""}
            onChange={(e) => setForm({ ...form, advanceAmount: Number(e.target.value) })}
          />
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              className="accent-orange-500"
              checked={form.withoutAdvance}
              onChange={(e) => setForm({ ...form, withoutAdvance: e.target.checked })}
            />
            Generar contrato sin anticipo
          </label>
          <textarea
            className="rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none md:col-span-2"
            rows="3"
            placeholder="Notas adicionales"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <div className="md:col-span-2">
            <label className="block text-sm text-orange-100/70 mb-1">Comprobante de pago (opcional)</label>
            <input type="file" accept="image/*,.pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} />
          </div>
          <button
            className="rounded-lg bg-gradient-to-r from-orange-500 to-amber-400 px-6 py-3 font-bold text-black transition-transform hover:scale-[1.02] md:col-span-2"
            type="submit"
          >
            Enviar Reserva
          </button>
        </form>
      </div>

      {/* Calendar hint */}
      <div className="rounded-2xl border border-orange-400/20 bg-black/20 p-6">
        <h2 className="font-bold uppercase">Fechas ya reservadas</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {reservedDates.length === 0 && <span className="text-sm text-orange-100/50">Todas las fechas disponibles.</span>}
          {reservedDates.map((d) => (
            <span key={d} className="rounded-full bg-orange-500/15 px-3 py-1 text-xs font-medium">
              {new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
